/**
 * Console-only palette. This is a deliberate, owner-approved exception to
 * the super-admin ruby-on-light rule and applies ONLY to /console routes.
 */
export const CONSOLE_THEME = {
  page: '#0D1117',
  panel: '#161B22',
  panelDeep: '#010409',
  border: '#30363D',
  borderSoft: '#21262D',
  text: '#E6EDF3',
  dim: '#8B949E',
  faint: '#484F58',
  ruby: '#DC143C',
  rubyLight: '#FF6B8A',
  green: '#3FB950',
  amber: '#D29922',
  red: '#F85149',
  mono: "'JetBrains Mono', ui-monospace, monospace",
  sans: "'IBM Plex Sans', system-ui, sans-serif",
} as const;

/** Surfaces used only by the console chrome, straight off the artboards. */
export const CONSOLE_EXTRA = {
  /** The DEV stripe's background and its border. */
  devStripe: '#2D1418',
  devStripeBorder: '#5C1A28',
  devStripeText: '#FFA8B8',
  /** Verdict "it's fixed" green, and the status bar when a run has passed. */
  greenSolid: '#238636',
  /** Status bar while an attempt is still running. */
  amberSolid: '#9E6A03',
  /** Inline diff line backgrounds. */
  diffAddBg: '#12261A',
  diffDelBg: '#2D1418',
  /** File names and URLs in the transcript. */
  link: '#79C0FF',
  quiet: '#C9D1D9',
} as const;

/**
 * Ruby's animations. Injected once by the console shell rather than living in
 * a stylesheet, because the console is the only surface that uses them and a
 * generic spinner is explicitly not allowed here.
 */
export const CONSOLE_KEYFRAMES = `
@keyframes gemTurn   { 0%,100% { transform: rotateY(0deg) scale(1) } 50% { transform: rotateY(180deg) scale(1.12) } }
@keyframes gemGlow   { 0%,100% { filter: drop-shadow(0 0 0 rgba(220,20,60,0)) } 50% { filter: drop-shadow(0 0 7px rgba(220,20,60,0.85)) } }
@keyframes gemSweep  { 0% { transform: translateX(-140%) } 60%,100% { transform: translateX(240%) } }
@keyframes rubyDot   { 0%,60%,100% { opacity: .25; transform: translateY(0) } 30% { opacity: 1; transform: translateY(-3px) } }
@keyframes rubyCaret { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
@keyframes rubyRing  { 0%,100% { box-shadow: 0 0 0 0 rgba(220,20,60,0.55) } 50% { box-shadow: 0 0 0 6px rgba(220,20,60,0) } }
@keyframes rubyPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(210,153,34,0.5) } 50% { box-shadow: 0 0 0 9px rgba(210,153,34,0) } }
@keyframes rubyFail  { 0%,100% { box-shadow: 0 0 0 0 rgba(248,81,73,0.5) } 50% { box-shadow: 0 0 0 9px rgba(248,81,73,0) } }
@keyframes rubyFlow     { from { background-position: 0 0 } to { background-position: 28px 0 } }
@keyframes rubyFlowBack { from { background-position: 28px 0 } to { background-position: 0 0 } }
@keyframes rubyGrow  { from { width: 8% } to { width: 64% } }
@keyframes rubyBlink { 0%,49% { opacity: 1 } 50%,100% { opacity: 0 } }
.ruby-gem-think { animation: gemTurn 2.1s ease-in-out infinite, gemGlow 2.1s ease-in-out infinite; }
.ruby-sweep-wrap { position: relative; overflow: hidden; border-radius: 6px; }
.ruby-sweep { position: absolute; top: 0; bottom: 0; width: 34%;
  background: linear-gradient(90deg, transparent, rgba(255,107,138,0.16), transparent);
  animation: gemSweep 2.4s ease-in-out infinite; }
.rt-console-search::placeholder { color: #6E7681; }
.rt-console-search:focus { border-color: #DC143C !important; box-shadow: 0 0 0 3px rgba(220,20,60,0.18); }
.rt-console-search::-webkit-search-cancel-button { display: none; }
`;
